import { motion } from "framer-motion";
import PageTransition from "@/components/ui/PageTransition";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const currentDate = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  return (
    <PageTransition>
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* Back Button */}
        <button 
          onClick={() => navigate(-1)}
          className="fixed top-4 left-4 p-2 flex items-center text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 mr-1" />
          Back
        </button>

        <div className="max-w-4xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Privacy Policy for Tivo Asset Manager</h1>
            <p className="text-lg text-gray-600">Last Updated: {currentDate}</p>
          </div>

          <div className="prose prose-blue max-w-none">
            <p className="text-gray-600 mb-8 text-lg">
              Welcome to Tivo Asset Manager ("Tivo," "we," "us," or "our"). We are committed to protecting your privacy and ensuring 
              the security of your personal and business data. This Privacy Policy outlines how we collect, use, disclose, and safeguard 
              your information when you use our platform, services, and NFC technology. By using Tivo Asset Manager, you agree to the 
              terms of this Privacy Policy.
            </p>

            <hr className="my-8 border-gray-200" />

            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-6">1. Information We Collect</h2>
              <p className="text-gray-600 mb-4">
                We collect the following types of information to provide and improve our services:
              </p>

              <h3 className="text-xl font-medium mb-4">Personal Information</h3>
              <ul className="list-disc pl-6 text-gray-600 mb-6">
                <li><strong>Account Information</strong>: Name, email address, phone number, and other details you provide during registration.</li>
                <li><strong>Team Collaboration Data</strong>: Information about team members, including their names, email addresses, and access permissions.</li>
              </ul>

              <h3 className="text-xl font-medium mb-4">Asset Information</h3>
              <ul className="list-disc pl-6 text-gray-600 mb-6">
                <li><strong>Asset Details</strong>: Descriptions, maintenance records, warranty information, repair history, and other data related to your assets.</li>
                <li><strong>NFC Tag Data</strong>: Information stored on NFC tags, such as asset IDs, QR codes, and linked documentation.</li>
              </ul>

              <h3 className="text-xl font-medium mb-4">Usage Data</h3>
              <ul className="list-disc pl-6 text-gray-600 mb-6">
                <li><strong>Log Data</strong>: IP addresses, browser type, operating system, and usage patterns when you interact with our platform.</li>
                <li><strong>Scan Data</strong>: Timestamps and locations of NFC tag scans for tracking and analytics purposes.</li>
              </ul>

              <h3 className="text-xl font-medium mb-4">Third-Party Data</h3>
              <ul className="list-disc pl-6 text-gray-600">
                <li><strong>Integration Data</strong>: Information from third-party services (e.g., cloud storage, CRM systems) that you connect to Tivo Asset Manager.</li>
              </ul>
            </section>

            <hr className="my-8 border-gray-200" />

            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-6">2. How We Use Your Information</h2>
              <p className="text-gray-600 mb-4">We use your information for the following purposes:</p>
              <ul className="list-disc pl-6 text-gray-600">
                <li>To enable asset tracking, team collaboration, and secure access to your data.</li>
                <li>To enhance the functionality, usability, and performance of Tivo Asset Manager.</li>
                <li>To send updates, notifications, and support-related messages.</li>
                <li>To analyze usage trends and improve our services.</li>
                <li>To protect your data and prevent unauthorized access or misuse.</li>
              </ul>
            </section>

            <hr className="my-8 border-gray-200" />

            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-6">3. How We Share Your Information</h2>
              <p className="text-gray-600 mb-4">We do not sell your personal or asset data. However, we may share information in the following circumstances:</p>
              <ul className="list-disc pl-6 text-gray-600">
                <li><strong>With Your Consent</strong>: When you authorize sharing with team members or third-party services.</li>
                <li><strong>Service Providers</strong>: With trusted vendors who assist in operating our platform (e.g., cloud hosting, analytics).</li>
                <li><strong>Legal Compliance</strong>: To comply with applicable laws, regulations, or legal processes.</li>
                <li><strong>Business Transfers</strong>: In the event of a merger, acquisition, or sale of assets, your data may be transferred to the new owner.</li>
              </ul>
            </section>

            <hr className="my-8 border-gray-200" />

            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-6">4. Data Security</h2>
              <p className="text-gray-600 mb-4">We implement enterprise-grade security measures to protect your information, including:</p>
              <ul className="list-disc pl-6 text-gray-600">
                <li><strong>Encryption</strong>: Data is encrypted in transit and at rest.</li>
                <li><strong>Access Controls</strong>: Role-based access to ensure only authorized users can view or manage assets.</li>
                <li><strong>Regular Audits</strong>: We conduct security assessments to identify and address vulnerabilities.</li>
              </ul>
            </section>

            <hr className="my-8 border-gray-200" />

            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-6">5. Your Rights and Choices</h2>
              <p className="text-gray-600 mb-4">You have the following rights regarding your data:</p>
              <ul className="list-disc pl-6 text-gray-600">
                <li><strong>Access and Update</strong>: You can view and update your personal and asset information through your account settings.</li>
                <li><strong>Delete Data</strong>: You may request the deletion of your account and associated data, subject to legal obligations.</li>
                <li><strong>Opt-Out</strong>: You can unsubscribe from promotional emails or disable certain data collection features.</li>
              </ul>
            </section>

            <hr className="my-8 border-gray-200" />

            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-6">6. Third-Party Services</h2>
              <p className="text-gray-600">
                Tivo Asset Manager may integrate with third-party services (e.g., cloud storage, CRM systems). 
                These services have their own privacy policies, and we encourage you to review them before use.
              </p>
            </section>

            <hr className="my-8 border-gray-200" />

            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-6">7. Children's Privacy</h2>
              <p className="text-gray-600">
                Our platform is not intended for use by individuals under the age of 16. 
                We do not knowingly collect personal information from children.
              </p>
            </section>

            <hr className="my-8 border-gray-200" />

            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-6">8. International Data Transfers</h2>
              <p className="text-gray-600">
                Your data may be processed and stored in countries outside your jurisdiction. 
                We ensure adequate safeguards are in place to protect your information.
              </p>
            </section>

            <hr className="my-8 border-gray-200" />

            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-6">9. Changes to This Privacy Policy</h2>
              <p className="text-gray-600">
                We may update this Privacy Policy periodically. The latest version will be posted on our website 
                with the effective date. Continued use of Tivo Asset Manager constitutes acceptance of the updated policy.
              </p>
            </section>

            <hr className="my-8 border-gray-200" />

            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-6">10. Contact Us</h2>
              <p className="text-gray-600 mb-4">
                If you have questions or concerns about this Privacy Policy or your data, please contact us at:
              </p>
              <ul className="list-none pl-6 text-gray-600">
                <li><strong>Email</strong>: <a href="mailto:privacy@tivoasset.com" className="text-blue-600 hover:text-blue-800">info@tivo.bio</a></li>
              </ul>
            </section>

            <hr className="my-8 border-gray-200" />

            <div className="text-center text-gray-600 mt-16">
              <p className="mb-4">
                Thank you for trusting Tivo Asset Manager with your data. We are dedicated to helping you manage 
                your assets with elegance, security, and peace of mind.
              </p>
              <p>
                © {new Date().getFullYear()} Tivo Asset Manager. All rights reserved.{" "}
                <a 
                  href="https://www.whisperlabs.ai" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800"
                >
                  Whisper Labs
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </PageTransition>
  );
};

export default PrivacyPolicy; 